import React from "react";

import { Stack } from "@mui/material";

import { FieldLabel, FieldLabelProps } from "../FieldLabel";
import { Switch } from "../Switch";

export interface ToggleFieldProps {
  /** Field title, rendered ABOVE the toggle (via the shared FieldLabel). */
  label?: React.ReactNode;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  /** Optional inline adornment right of the label (info icon, "(optional)", …). */
  adornment?: React.ReactNode;
  /** Label typography — defaults to body2 to match filter-drawer field titles. */
  labelVariant?: FieldLabelProps["variant"];
  /** Height of the control row the switch is vertically centered in — matches the
   *  sibling fields' input height so the toggle lines up (and gets breathing room
   *  below the title). */
  controlMinHeight?: number;
}

/**
 * VERA-455 C — shared toggle field: title ABOVE, switch BELOW, with the canonical
 * FieldLabel gap (§31). One component so every "Show archived"-style toggle inherits
 * identical label placement + spacing instead of each page hand-rolling a
 * Typography + Switch (products used a label-beside FormControlLabel; certs a
 * hand-rolled title-above Stack). Change the label/gap in FieldLabel → all update.
 */
export const ToggleField = ({
  label,
  checked = false,
  onChange,
  disabled = false,
  adornment,
  labelVariant = "body2",
  controlMinHeight = 44,
}: ToggleFieldProps) => (
  <Stack>
    <FieldLabel label={label} adornment={adornment} variant={labelVariant} />
    {/* Center the switch in a field-height row so it aligns with the sibling fields'
        inputs and sits a comfortable distance below the title (VERA-455). */}
    <Stack sx={{ minHeight: controlMinHeight }} justifyContent="center">
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange?.(e.target.checked)}
      />
    </Stack>
  </Stack>
);
